import React, {useEffect, useState, useRef, useMemo} from 'react'
import {MapContainer, TileLayer, GeoJSON, Tooltip} from 'react-leaflet'
import styled from 'styled-components'
import 'leaflet/dist/leaflet.css'
import axios from 'axios'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import InfoIcon from '@mui/icons-material/Info'
interface OccupationData {
  SOC: string
  Description: string
  '2022 Resident Workers': number
  '2022 Net Commuters': number
  County: string
}
interface StyledLoaderProps {
  loading: boolean
}

interface CategoryDataItem {
  [key: string]: number | string | undefined
}

const categoryKeys = [
  'Construction',
  'Broadband',
  'Clean Energy',
  'Conservation',
  'IRA Clean Energy',
  'IRA Clean Manufacturing',
  'IRA Efficient Building Jobs',
  'IRA Environmental Justice Jobs',
]

const StyledLoader = styled.div<StyledLoaderProps>`
  display: ${(props) => (props.loading ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1000;
`
const StyledHeading = styled.h5`
  margin-top: 20px; // Adjust this value as needed for spacing
  margin-bottom: 10px; // Adjust this value as needed for spacing
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  border: 1px solid black;
  background-color: #f3f3f3;
  color: black;
  font-size: 16px;
  appearance: none;
  cursor: pointer;
`

const StyledRightContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 400px;
`

const StyledTitle = styled.h1`
  position: absolute;
  top: 0;
  left: 0;
  margin: 10px;
  z-index: 1000; // To ensure it appears above the map
`

const StyledLegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 18px;
`

const StyledSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  margin-bottom: 20px;
`

const StyledLegendColorBox = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 10px;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row-reverse; // Keeps the current layout for larger screens

  @media (max-width: 768px) {
    // Adjusts for smaller screens like tablets and mobile devices
    flex-direction: column;
  }
`

const StyledInfoContainer = styled.div`
  background-color: #f3f3f3;
  padding: 10px;
  border-radius: 5px;
  border: 2px solid black;
  margin-bottom: 10px;
`

const StyledSideBar = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
  padding: 20px;
`

const StyledNegativeText = styled.div`
  color: red;
`

const StyledMapContainer = styled.div`
  flex-grow: 1;
`

const mapStyle = {height: '100vh', width: '100%', margin: '0 auto'}

const CommuterMap: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [countyData, setCountyData] = useState(null)
  const geoUrl = process.env.PUBLIC_URL + '/colorado-counties.geojson'
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null)
  const selectedCountyRef = useRef(selectedCounty)
  const [minMaxCommute, setMinMaxCommute] = useState({min: 0, max: 0})
  const [occupationData, setOccupationData] = useState<OccupationData[]>([])
  const [selectedSOC, setSelectedSOC] = useState<string | null>(null)
  const [categoryData, setCategoryData] = useState<CategoryDataItem[]>([])

  const [openInfo, setOpenInfo] = useState(false)
  const [mapZoom, setMapZoom] = useState(7.3) // Default zoom level
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Handler functions for the info dialog
  const handleClickOpenInfo = () => {
    setOpenInfo(true)
  }

  const handleCloseInfo = () => {
    setOpenInfo(false)
  }

  // Step 1: Create a new state for the filtered occupation list
  // Set initial state of filteredOccupations to the same as occupationData
  const [filteredOccupations, setFilteredOccupations] = useState<OccupationData[]>(occupationData)

  // Step 2: Update the handleCategoryClick function
  // Update the handleCategoryClick function
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)

    if (category === 'All') {
      // If 'All' category is selected, reset the filter to show all occupations
      setFilteredOccupations(occupationData)
    } else {
      // Filter based on the selected category
      const filteredData = occupationData.filter((occupation) => {
        const categoryItem = categoryData.find((cat) => cat.SOC === occupation.SOC)
        return categoryItem && categoryItem[category] === 1
      })

      setFilteredOccupations(filteredData)
    }
  }

  // Define fetchOccupationData
  const fetchOccupationData = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_ENDPOINT + '/occupation_data_colorado'
      )
      return response.data
    } catch (error) {
      console.error('Error fetching occupation data:', error)
      return []
    }
  }

  // Define fetchCountyData
  const fetchCountyData = async () => {
    try {
      const response = await axios.get(geoUrl)
      return response.data
    } catch (error) {
      console.error('Error fetching county data:', error)
      return null
    }
  }
  useEffect(() => {
    if (occupationData.length > 0) {
      console.log('Sample occupation data item:', occupationData[0])
    }
  }, [occupationData])

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth
      if (currentWidth < 768) {
        // Mobile devices width threshold
        setMapZoom(6) // Zoom out for mobile devices
      } else {
        setMapZoom(7.3) // Default zoom for larger screens
      }
    }

    // Set the initial zoom based on the current window size
    handleResize()

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Clean up
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Combined useEffect for fetching all data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [categoriesResponse, occupationDataResponse, countyDataResponse] = await Promise.all([
          axios.get(process.env.REACT_APP_API_ENDPOINT + '/iija_categories'),
          axios.get(process.env.REACT_APP_API_ENDPOINT + '/occupation_data_colorado'),
          axios.get(geoUrl),
        ])

        setCategoryData(categoriesResponse.data)
        console.log(categoriesResponse.data)
        const occupationData = occupationDataResponse.data
        setOccupationData(occupationData)
        setCountyData(countyDataResponse.data)
        setSelectedSOC(occupationData.length > 0 ? occupationData[0].SOC : null)
        // Set filteredOccupations with fetched data
        setFilteredOccupations(occupationData)

        // Optionally, set a default selected occupation
        if (occupationData.length > 0) {
          setSelectedSOC(occupationData[1350].SOC)
        }
        const netCommutes = occupationData.map((d) => d['2022 Net Commuters'])
        setMinMaxCommute({
          min: Math.min(...netCommutes),
          max: Math.max(...netCommutes),
        })
      } catch (error) {
        console.error('Error fetching data:', error)
      }
      setLoading(false)
    }

    fetchData()
    setFilteredOccupations(occupationData)
  }, [geoUrl])

  const selectedSOCRef = useRef(selectedSOC)

  useEffect(() => {
    selectedSOCRef.current = selectedSOC
  }, [selectedSOC])

  useEffect(() => {
    if (selectedSOC) {
      const socSpecificCommutes = occupationData
        .filter((d) => d.SOC === selectedSOC)
        .map((d) => d['2022 Net Commuters'])
      setMinMaxCommute({
        min: Math.min(...socSpecificCommutes),
        max: Math.max(...socSpecificCommutes),
      })
    }
  }, [selectedSOC, occupationData])

  // give a console log of the categoryData
  useEffect(() => {
    console.log('categoryData:', categoryData)
  }, [categoryData])

  //Richard's funciton will take a string and reverse it
  const reverseString = (str: string) => {
    return str.split('').reverse().join('')
  }

  // useEffect(() => {
  //   Promise.all([fetchOccupationData(), fetchCountyData()]).then(([occupationData, countyData]) => {
  //     setOccupationData(occupationData)
  //     setCountyData(countyData)
  //     setSelectedSOC(occupationData.length > 0 ? occupationData[0].SOC : null)
  //     const netCommutes = occupationData.map((d) => d['2022 Net Commuters'])
  //     setMinMaxCommute({
  //       min: Math.min(...netCommutes),
  //       max: Math.max(...netCommutes),
  //     })
  //     setLoading(false)
  //   })
  // }, [])

  useEffect(() => {
    console.log('categoryKeys:', categoryKeys)

    if (categoryData.length > 0) {
      const sampleItem = categoryData[0]
      const sampleItemKeys = Object.keys(sampleItem)

      console.log('Keys in sample categoryData item:', sampleItemKeys)

      // Checking if all categoryKeys exist in sampleItemKeys
      const allKeysMatch = categoryKeys.every((key) => sampleItemKeys.includes(key))
      console.log('Do all categoryKeys exist in categoryData item?', allKeysMatch)

      // Check for additional keys in categoryData items not in categoryKeys
      const additionalKeys = sampleItemKeys.filter(
        (key) => !categoryKeys.includes(key) && key !== 'SOC' && key !== 'Description'
      )
      console.log('Additional keys in categoryData item not in categoryKeys:', additionalKeys)
    }
  }, [categoryData, categoryKeys])

  const getNetCommutersForCounty = (countyName: string, selectedSOC: string) => {
    if (!selectedSOC) return 0
    const occupationEntry = occupationData.find(
      (d) => d.County === countyName && d.SOC === selectedSOC
    )
    return occupationEntry ? occupationEntry['2022 Net Commuters'] : 0
  }

  const generateColorScale = (minVal: number, maxVal: number) => {
    const positiveColors = ['#416FAA', '#8BBEDC', '#B2D3E4', '#E2EBF4'].reverse()
    const negativeColors = ['#F8C7B4', '#F8A991', '#EE675C', '#D34D51']

    const positiveStep = maxVal / positiveColors.length
    const negativeStep = Math.abs(minVal) / negativeColors.length

    const positiveIntervals = Array.from(
      {length: positiveColors.length},
      (_, i) => (i + 1) * positiveStep
    )
    const negativeIntervals = Array.from(
      {length: negativeColors.length},
      (_, i) => -(i + 1) * negativeStep
    )

    return [
      ...negativeIntervals.map((interval, i) => ({interval, color: negativeColors[i]})),
      ...positiveIntervals.map((interval, i) => ({interval, color: positiveColors[i]})),
    ]
  }

  const colorScale = useMemo(() => {
    const scale = generateColorScale(minMaxCommute.min, minMaxCommute.max)
    return scale
  }, [minMaxCommute])

  const getCommuterColor = (commuters: number) => {
    for (const {interval, color} of colorScale) {
      if (commuters === 0) return '#E2EBF4'
      if (commuters <= interval && commuters > 0) {
        return color
      } else if (commuters >= interval && commuters < 0) {
        return color
      }
    }
    return commuters > 0 ? colorScale[colorScale.length - 1].color : colorScale[0].color
  }

  const getPopupContent = (feature: any, selectedSOC: string | null) => {
    if (!selectedSOC)
      return `<strong>${feature.properties.name}</strong><br>Unknown Occupation<br><span style="font-size: 1.2em; font-weight: bold;">0</span> Commuters`

    const occupation = occupationData.find(
      (d) => d.County === feature.properties.name && d.SOC === selectedSOC
    )
    let commuters = occupation ? occupation['2022 Net Commuters'] : 0

    commuters = commuters.toFixed(0) === '-0' ? 0 : commuters

    const occupationName = occupation ? occupation.Description : 'Unknown Occupation'

    return `<strong>${
      feature.properties.name
    }</strong><br>${occupationName}<br><span style="font-size: 1.2em; font-weight: bold;">${commuters.toFixed(
      0
    )}</span> Commuters`
  }

  const onEachFeature = (feature: any, layer: any) => {
    const currentSelectedSOC = selectedSOCRef.current
    if (!currentSelectedSOC) return

    layer.bindPopup(getPopupContent(feature, currentSelectedSOC))

    layer.on('popupopen', () => {
      layer.setPopupContent(getPopupContent(feature, selectedSOCRef.current))
    })
  }

  const countyStyle = (feature: any) => {
    if (!selectedSOC) {
      return {
        fillColor: '#ffffff',
        weight: 1,
        color: 'black',
        dashArray: '3',
        fillOpacity: 0.6,
      }
    }
    const commuters = getNetCommutersForCounty(feature.properties.name, selectedSOC)
    const color = getCommuterColor(commuters)
    return {
      fillColor: getCommuterColor(commuters),
      weight: 1,
      opacity: 3,
      color: 'black',
      dashArray: '3',
      fillOpacity: selectedCounty === feature.properties.name ? 1 : 0.9,
    }
  }

  const Legend: React.FC<{threshold: number}> = ({threshold}) => (
    <StyledInfoContainer>
      <div style={{fontWeight: 'bold', marginBottom: '10px', fontSize: 16}}>
        Workers Entering County
      </div>
      {colorScale
        .filter(({interval}) => interval > 0)
        .reverse()
        .map((item, index, arr) => (
          <StyledLegendItem key={index}>
            <StyledLegendColorBox style={{backgroundColor: item.color}}></StyledLegendColorBox>
            <div>
              {index === arr.length - 1
                ? `0 to ${item.interval.toFixed(0)}`
                : `${arr[index + 1]?.interval.toFixed(0)} to ${item.interval.toFixed(0)}`}
            </div>
          </StyledLegendItem>
        ))}

      <div style={{fontWeight: 'bold', marginBottom: '10px', fontSize: 16}}>
        Workers Leaving the Area
      </div>
      {colorScale
        .filter(({interval}) => interval <= 0)
        .map((item, index, arr) => (
          <StyledLegendItem key={index}>
            <StyledLegendColorBox style={{backgroundColor: item.color}}></StyledLegendColorBox>
            <StyledNegativeText>
              {index === 0
                ? `0 to ${item.interval.toFixed(0)}`
                : `${item.interval.toFixed(0)} to ${arr[index - 1]?.interval.toFixed(0)}`}
            </StyledNegativeText>
          </StyledLegendItem>
        ))}
    </StyledInfoContainer>
  )
  return (
    <>
      <div className='card'>
        <h1>IIJA / IRA Workforce Net Commuter Map</h1>
        {loading || !countyData ? (
          <StyledLoader loading={loading} id='splash-screen' className='splash-screen'>
            <img src={process.env.PUBLIC_URL + '/media/logos/LumWheel2.png'} alt='Luminance logo' />
            <span>Loading ...</span>
          </StyledLoader>
        ) : (
          <StyledContainer>
            <StyledRightContainer>
              {/* New container for the sidebar and dropdown */}
              <StyledSelectContainer>
                {/* New heading for category selection */}
                <StyledHeading>Select Category</StyledHeading>

                {/* Category buttons */}
                {categoryKeys.map((key, index) => (
                  <Button
                    key={index}
                    onClick={() => handleCategoryClick(key)}
                    variant='contained'
                    color={selectedCategory === key ? 'primary' : 'default'}
                    style={{marginBottom: '5px'}}
                  >
                    {key}
                  </Button>
                ))}

                {/* Heading for occupation selection */}
                <StyledHeading>Select Occupation</StyledHeading>
                <StyledSelect
                  value={selectedSOC || ''}
                  onChange={(e) => setSelectedSOC(e.target.value)}
                >
                  {filteredOccupations
                    .filter(
                      (data, index, self) => index === self.findIndex((t) => t.SOC === data.SOC)
                    )
                    .map((filteredData, index) => (
                      <option key={`${filteredData.SOC}-${index}`} value={filteredData.SOC}>
                        {filteredData.SOC} - {filteredData.Description}
                      </option>
                    ))}
                </StyledSelect>
              </StyledSelectContainer>
              <StyledSideBar>
                <Legend threshold={10000000} />
              </StyledSideBar>
              {/* Add this Icon button where you want to display the info icon */}
              <div
                onClick={handleClickOpenInfo}
                style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}
              >
                <IconButton color='primary'>
                  <InfoIcon />
                </IconButton>
                <span>Information</span>
              </div>
              {/* Info Dialog */}
              <Dialog open={openInfo} onClose={handleCloseInfo}>
                <DialogTitle>Net Migration Information</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    This map illustrates the net migration for specific occupations categorized
                    under the IIJA. You can choose from the IIJA Occupation list to see where people
                    reside compared to where they work. Areas marked in red indicate negative
                    migration, indicating that people live there but work elsewhere, while blue
                    areas show people commuting into the county for work but not living there. It's
                    worth noting that, from a data perspective, this map uses a choropleth
                    representation with values constrained to the highest and lowest migration
                    figures to emphasize the maximum variation in each county visually.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseInfo} color='primary'>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </StyledRightContainer>
            {/* End of new container */}
            <StyledMapContainer>
              <MapContainer
                center={[38, -105.15887]}
                zoom={mapZoom}
                scrollWheelZoom={false}
                style={mapStyle}
                maxZoom={7.3}
              >
                <TileLayer
                  attribution='Map tiles by Carto, under CC BY 3.0. Data by OpenStreetMap, under ODbL.'
                  url='https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png'
                />
                {countyData && (
                  <GeoJSON data={countyData} style={countyStyle} onEachFeature={onEachFeature} />
                )}
              </MapContainer>
            </StyledMapContainer>
          </StyledContainer>
        )}
        <a>SECTION SOURCE: Lightcast Data Pull Q3: https://lightcast.io/</a>
      </div>
    </>
  )
}

export default CommuterMap
